/**
 * Whether a profile already has a front-of-ID photo, and who the automatic capture should be
 * attributed to. Pure predicates over the response shape — no transport, no state, no policy.
 *
 * The whole point of this layer is which way each question fails when the server says nothing.
 * `HasIdPhoto` is new on the response, so an older backend, a partially-projected candidate, or a
 * shape this client has not seen yet all arrive as `undefined`. Two different defaults are correct,
 * because the two questions are not the same question:
 *
 * - For a profile that already exists, silence means *unknown*, and an unknown profile is left
 *   alone. Prompting for a photo the profile already has is a worse outcome than not prompting:
 *   it overwrites a good scan-time photo with whatever the counter camera manages, and it costs
 *   staff a step on every returning customer. So `needsFrontIdPhoto` is true only when the server
 *   said `false` — explicitly, for that profile.
 * - For a profile this scan just created, silence means *there cannot be one yet*. The profile did
 *   not exist a moment ago, so absent presence is the normal case rather than a gap, and the only
 *   answer that should suppress the prompt is the server explicitly saying it already stored one.
 *   So `newProfileNeedsFrontIdPhoto` is true unless the server said `true`.
 *
 * Read that second predicate as narrowly as it is written: it is only sound at a call site that
 * already knows the profile was created by the scan in hand. Applied to an existing profile it
 * would prompt for every customer whose presence the server omitted, which is the opposite of the
 * first predicate's intent.
 *
 * Re-exported by `camera-id-scan.ts`, which stays the single import site for both surfaces, and
 * also importable directly as `@dutchie-pos/requests/checkin/id-photo-presence`.
 */
import type { EnteredEntry } from './scan-entry-result';

/**
 * The one field any of this reads. Structural on purpose: every candidate shape on the response —
 * the identified customer, a matched customer, a caregiver — answers this question the same way,
 * and nothing here needs the other forty fields to do it.
 *
 * Nullable as well as optional because the server projects an unknown presence as JSON `null` on
 * some shapes and omits the key on others. Both mean the same thing here, and both are told apart
 * from `false`, which is the only answer that says a profile is genuinely missing its photo.
 */
export type IdPhotoPresence = {
  HasIdPhoto?: boolean | null;
};

/**
 * Does this existing profile need a front-of-ID photo? True only when the server said it has none.
 *
 * `undefined`, `null` and `true` are all false: the first two are unknown and the third is a
 * profile that already has one.
 */
export const needsFrontIdPhoto = (customer: IdPhotoPresence | null | undefined): boolean =>
  customer?.HasIdPhoto === false;

/**
 * Does this just-created profile need a front-of-ID photo? True unless the server said it already
 * has one.
 *
 * Only sound where the caller already established the profile was created by the scan in hand.
 * See the module comment: for anything else, use `needsFrontIdPhoto`.
 */
export const newProfileNeedsFrontIdPhoto = (profile: IdPhotoPresence | null | undefined): boolean =>
  profile?.HasIdPhoto !== true;

/** The least a candidate has to expose to be asked about: which profile it is, and its presence. */
export type PresenceCandidate = IdPhotoPresence & { Id?: number | null };

/**
 * Does the candidate staff just picked need a front-of-ID photo?
 *
 * This is the predicate for an *explicit decision*, where the response offered several records and a
 * person chose one. It exists because one of those records may not be an existing profile at all:
 * when the pipeline created a profile and stopped short of the check-in, that new profile comes back
 * as a candidate under `CreatedCustomerId`, sitting in the same list as real matches. Reading it
 * with `needsFrontIdPhoto` asks an existing-profile question about a profile that did not exist a
 * moment ago, so the server's silence is treated as unknown and the one customer who certainly has
 * no photo is the one never asked about.
 *
 * So the candidate's own identity picks the default, and `frontPhotoTarget` already draws the same
 * line for the automatic path:
 *
 * - The candidate *is* the profile this scan created: silence means there cannot be a photo yet, so
 *   it is offered unless the server explicitly said it already has one.
 * - Any other candidate: an existing profile, offered only on an explicit `HasIdPhoto === false`.
 *
 * `HasIdPhoto: true` suppresses either way — that answer is not a default, it is the server saying a
 * photo is already stored, and overwriting it is the outcome this whole layer avoids.
 *
 * Fail-closed on anything it cannot line up. No candidate, a candidate with no usable id, or a
 * response with no created id all fall back to the existing-profile question, which is the quiet
 * one: matching a missing id against a missing created id would otherwise make every unidentifiable
 * candidate look freshly created and prompt for all of them.
 */
export const needsFrontIdPhotoForCandidate = (
  entry: EnteredEntry | null | undefined,
  candidate: PresenceCandidate | null | undefined,
): boolean => {
  const created = entry?.CreatedCustomerId ?? null;
  const candidateId = candidate?.Id ?? null;
  const isCreatedProfile = Boolean(created) && candidateId === created;

  return isCreatedProfile ? newProfileNeedsFrontIdPhoto(candidate) : needsFrontIdPhoto(candidate);
};

/**
 * Every record on the response that names this id. Plural by necessity: the identified customer and
 * a matched candidate can both describe the same profile, and only the response knows whether they
 * agree about its photo.
 */
const recordsFor = (entry: EnteredEntry | null | undefined, id: number): IdPhotoPresence[] => {
  const identified = entry?.ValidatedIdentificationCustomer ?? null;
  const matched = (entry?.ValidatedMatchedCustomers ?? []).filter((candidate) => candidate.Id === id);

  return identified && identified.Id === id ? [identified, ...matched] : matched;
};

/** A candidate the server itself says cannot be selected. It cannot also be who it checked in. */
const bannedAnywhere = (entry: EnteredEntry | null | undefined, id: number): boolean =>
  (entry?.ValidatedMatchedCustomers ?? []).some((candidate) => candidate.Id === id && candidate.IsBanned);

/**
 * Who the *automatic* front-of-ID photo should be written to, or null to write nothing.
 *
 * Identity comes from the server. `ResolvedCustomerId` (leaflogix #22416, camera source only) is the
 * profile the pipeline created or the ranked match it actually checked in, so this function does not
 * re-derive who an entry landed on and deliberately does not consult `enteredCustomerId`. That
 * resolver still backs the manual photo opt-in both surfaces ship today, unchanged; the difference
 * is that the manual path has staff standing behind its answer, and an automatic capture has
 * nobody. What this adds on top of the anchor is one question the anchor cannot answer: does that
 * profile actually need a photo?
 *
 * The anchor is null-omitted on the wire, so an explicit decision and a backend that predates the
 * field are the same absent key. Both collapse into the same refusal, which is what makes the
 * ambiguity safe rather than something to detect:
 *
 * - An anchored created profile is offered unless the server explicitly said it already has a photo.
 * - An anchored existing profile is offered only when every record the response gives for that exact
 *   id says `HasIdPhoto === false`. No record at all (an unlisted or unprojected anchor) is unknown,
 *   not missing; records that disagree are unknown too; a candidate the server marked banned is a
 *   contradiction of the anchor, since a banned customer cannot have been checked in.
 * - With no anchor, no existing customer is ever offered. The previous ranked-candidate inference is
 *   gone: guessing the top selectable match is exactly the kind of client-side attribution the
 *   server field exists to replace, and on an explicit decision it would prompt before staff have
 *   chosen anybody.
 * - With no anchor, a profile this scan created is still offered, but only once the response also
 *   says the check-in landed. That keeps an older backend working for the auto-create case — where
 *   the created id is unambiguous, because it exists only because of this scan — while refusing the
 *   create-then-decide shape, where the same id is a candidate staff have not picked yet.
 *
 * Fail-closed throughout, because the two failures are not symmetric. Not capturing a photo is
 * recoverable and visible — staff still have the manual opt-in — while writing one to the wrong
 * record, or over a profile's existing one, is neither.
 */
export const frontPhotoTarget = (entry: EnteredEntry | null | undefined): number | null => {
  const anchor = entry?.ResolvedCustomerId ?? null;
  const created = entry?.CreatedCustomerId ?? null;

  if (!anchor) {
    // No authoritative identity: either staff have still to choose, or this backend does not send
    // the anchor. A created profile that the response also says was checked in is the one case that
    // needs no inference at all, so it is the only fallback kept.
    if (!created || !entry?.CheckedInShipmentId) {
      return null;
    }

    return recordsFor(entry, created).every(newProfileNeedsFrontIdPhoto) ? created : null;
  }

  if (anchor === created) {
    // `every` over an empty list is the intended answer: a created profile with no presence
    // reported anywhere is exactly the case `newProfileNeedsFrontIdPhoto` exists to default.
    return recordsFor(entry, anchor).every(newProfileNeedsFrontIdPhoto) ? anchor : null;
  }

  const records = recordsFor(entry, anchor);
  if (records.length === 0 || bannedAnywhere(entry, anchor)) {
    return null;
  }

  return records.every(needsFrontIdPhoto) ? anchor : null;
};
